import { useCallback, useEffect } from "react";
import { useSelector } from "src/redux/hooks";
import { useIsOnline } from "../../hooks";
import dayjs from "dayjs";

interface UseAmbVoyageTotalsProps {
  id: string;
  setValue: (id: string, value: any) => void;
  rowIndex: number;
  tag?: string;
}

/**
 * Hook to calculate the total voyage distance for a specific voyage row
 * Handles both carried-over voyages from previous entries and new voyage rows
 */
const useAmbVoyageTotals = ({
  id,
  setValue,
  rowIndex,
  tag,
}: UseAmbVoyageTotalsProps) => {
  const isOnline = useIsOnline();
  const voyages = useSelector(state => state.generatorSlice.voyages);
  const prevEntryData = useSelector(
    state => state.generatorSlice.lbentry.prevEntryData,
  );
  const { entrydate } = useSelector(
    state => state.generatorSlice.entryDataToSave,
  );
  // Only run for amb2-voy-totals fields
  if (tag !== "amb2-voy-totals") {
    return;
  }

  const previousVoyage = prevEntryData?.list?.previousVoyage || null;

  const parseDateTime = (
    dateTimeStr: string | null | undefined,
  ): Date | null => {
    if (!dateTimeStr || dateTimeStr.trim() === "") return null;
    const parts = dateTimeStr.split(" - ");
    if (parts.length !== 2) return null;

    const dateStr = parts[0].trim();
    const timeStr = parts[1].trim();

    const date = new Date(dateStr);
    const [hours, minutes] = timeStr.split(":").map(part => parseInt(part, 10));
    date.setHours(hours, minutes, 0, 0);

    return date;
  };

  const isRowInVoyage = (
    rowTime: string,
    voyageStart: string | null,
    voyageEnd: string | null,
  ): boolean => {
    if (!rowTime || !entrydate) return false;

    const rowDateTime = new Date(entrydate);
    const [hours, minutes] = rowTime.split(":").map(part => parseInt(part, 10));
    rowDateTime.setHours(hours, minutes, 0, 0);

    const startDateTime = parseDateTime(voyageStart);
    const endDateTime = parseDateTime(voyageEnd);

    if (!startDateTime) return false;

    const isAfterStart = rowDateTime >= startDateTime;
    const isBeforeEnd = !endDateTime || rowDateTime <= endDateTime;

    return isAfterStart && isBeforeEnd;
  };

  const calculateVoyageTotal = useCallback(() => {
    if (!isOnline) return;

    let voyageStart: string | null = null;
    let voyageEnd: string | null = null;
    let previousTripLogs: number[] = [];

    if (
      rowIndex === 0 &&
      previousVoyage &&
      previousVoyage.startDate &&
      previousVoyage.startTime
    ) {
      voyageStart =
        dayjs(previousVoyage.startDate).format("DD MMM YYYY") +
        " - " +
        previousVoyage.startTime;
      voyageEnd = voyages[0]?.end || null;
      previousTripLogs = previousVoyage.previousTrip || [];
    } else {
      const voyage = voyages[rowIndex];
      if (!voyage || !voyage.start) {
        setValue(id, 0);
        return;
      }
      voyageStart = voyage.start;
      voyageEnd = voyage.end;
    }

    if (previousVoyage ? !voyageEnd : !voyageStart || !voyageEnd) {
      setValue(id, "");
      return;
    }

    // Find all trip log rows within voyage
    const allRows = Array.from(document.querySelectorAll(".tripLog"))
      .map(el => el.closest("tr"))
      .filter((tr): tr is HTMLTableRowElement => !!tr);

    const tripLogValues: number[] = [...previousTripLogs];

    allRows.forEach(row => {
      const timeInput = row.querySelector<HTMLInputElement>(
        'input[data-unit="time"], input[data-unit="24lock"]',
      );
      const tripLogInput = row.querySelector<HTMLInputElement>(
        ".tripLog input:not([disabled])",
      );

      if (
        !timeInput ||
        !tripLogInput ||
        !timeInput.value ||
        !tripLogInput.value
      ) {
        return;
      }

      // Extract time value
      let timeValue = timeInput.value;
      if (
        typeof timeValue === "object" &&
        timeValue !== null &&
        "time" in timeValue
      ) {
        timeValue = (timeValue as any).time;
      }

      if (
        typeof timeValue === "string" &&
        isRowInVoyage(timeValue, voyageStart, voyageEnd)
      ) {
        const tripValue = parseFloat(tripLogInput.value);
        if (!isNaN(tripValue)) {
          tripLogValues.push(tripValue);
        }
      }
    });

    let currentDistance = 0;
    if (tripLogValues.length > 0) {
      const firstValue = tripLogValues[0];
      const lastValue = tripLogValues[tripLogValues.length - 1];
      currentDistance = lastValue - firstValue;
      if (currentDistance < 0) currentDistance = 0;
    }
    setValue(id, currentDistance);
  }, [id, isOnline, rowIndex, voyages, previousVoyage, entrydate, setValue]);

  useEffect(() => {
    const calcInputs = Array.from(
      document.querySelectorAll(
        '.tripLog input, input[data-unit="time"], input[data-unit="24lock"]',
      ),
    ).filter(
      i =>
        !(i as HTMLInputElement).disabled &&
        !(i as HTMLInputElement).name.startsWith("overlay_"),
    );

    calcInputs.forEach(i => i.addEventListener("blur", calculateVoyageTotal));
    return () => {
      calcInputs.forEach(i =>
        i.removeEventListener("blur", calculateVoyageTotal),
      );
    };
  }, [calculateVoyageTotal]);

  useEffect(() => {
    calculateVoyageTotal();
  }, [calculateVoyageTotal, voyages, previousVoyage]);
};

export default useAmbVoyageTotals;
